import React, { createContext, useState, useContext, useCallback } from "react"
import type { LatLngBounds } from "leaflet"

interface MapContextType {
  totalExpectedLayers: number
  loadedLayers: number
  layerBounds: LatLngBounds[]
  registerLayer: (layerId: string) => void
  unregisterLayer: (layerId: string) => void
  markLayerLoaded: (layerId: string, bounds: LatLngBounds) => void
  resetLayerTracking: () => void
  setTotalExpectedLayers: (count: number) => void
  allLayersLoaded: boolean
}

const MapContext = createContext<MapContextType>({
  totalExpectedLayers: 0,
  loadedLayers: 0,
  layerBounds: [],
  registerLayer: () => {},
  unregisterLayer: () => {},
  markLayerLoaded: () => {},
  resetLayerTracking: () => {},
  setTotalExpectedLayers: () => {},
  allLayersLoaded: false,
})

export const MapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [totalExpectedLayers, setTotalExpectedLayers] = useState<number>(0)
  const [registeredLayers, setRegisteredLayers] = useState<Set<string>>(new Set())
  const [loadedLayers, setLoadedLayers] = useState<Set<string>>(new Set())
  const [layerBounds, setLayerBounds] = useState<LatLngBounds[]>([])

  const registerLayer = useCallback((layerId: string) => {
    setRegisteredLayers((prev) => {
      const newSet = new Set(prev)
      newSet.add(layerId)
      return newSet
    })
  }, [])

  const unregisterLayer = useCallback((layerId: string) => {
    setRegisteredLayers((prev) => {
      const newSet = new Set(prev)
      newSet.delete(layerId)
      return newSet
    })
    setLoadedLayers((prev) => {
      const newSet = new Set(prev)
      newSet.delete(layerId)
      return newSet
    })
  }, [])

  const markLayerLoaded = useCallback((layerId: string, bounds: LatLngBounds) => {
    setLoadedLayers((prev) => {
      const newSet = new Set(prev)
      newSet.add(layerId)
      return newSet
    })

    if (bounds) {
      setLayerBounds((prev) => [...prev, bounds])
    }
  }, [])

  const resetLayerTracking = useCallback(() => {
    setRegisteredLayers(new Set())
    setLoadedLayers(new Set())
    setLayerBounds([])
    setTotalExpectedLayers(0)
  }, [])

  const allLayersLoaded = totalExpectedLayers > 0 && loadedLayers.size === totalExpectedLayers

  return (
    <MapContext.Provider
      value={{
        totalExpectedLayers,
        loadedLayers: loadedLayers.size,
        layerBounds,
        registerLayer,
        unregisterLayer,
        markLayerLoaded,
        resetLayerTracking,
        setTotalExpectedLayers,
        allLayersLoaded,
      }}>
      {children}
    </MapContext.Provider>
  )
}

export const useMapContext = () => useContext(MapContext)

export default MapContext
